// utils/launchTarget.js
//
// Shared helpers for the Telegram → system-browser launch handoff.
// Used by the bot-deep-link landing page (TgLaunchPage), the in-app
// quick-launch FAB (OpenInBrowserFab) and the collector's Mini App screen
// (CollectorTmaScreen) so the URL shape and platform-detection logic stay in
// lockstep.
//
// The exit itself is Telegram's `openLink` with a BROWSER PREFERENCE
// (`tryBrowser: 'chrome'` on Android), never an `intent://` URL: Telegram
// Android refuses intents from a Mini App and opens their fallback inside its
// own WebView, where the single-use token is spent in the wrong cookie jar
// (owner research 2026-11-03, pack prompt 7 Part A). `buildChromeIntentUrl`
// stays for ONE other door: the "install in Chrome" sheet shown in Samsung
// Internet and the other Android browsers (Part B), which is a real browser
// tab and honours Chrome's intent rules.

import { adapter } from '@/platform/index.js';
import { issue } from '@/services/launchToken.service';
import { chromeIntentUrlFor } from '@/utils/browserEscape.js';

/**
 * Resolve the canonical browser URL for the launch landing page.
 *
 * We use the runtime origin so the URL always points at the same host the SPA
 * itself is served from — works for tunnel (mac.poolit.club), prod
 * (app.poolit.club), and preview deploys without env-file coordination.
 * Env-var fallbacks exist for unusual embedded contexts where `window` is
 * unavailable; production code should never hit them.
 *
 * @param {string} token - Single-use launch token from /auth/launch-token/issue.
 * @returns {string} Absolute https URL.
 */
export function buildLaunchUrl(token) {
  const base = (typeof window !== 'undefined' && window.location?.origin)
    || import.meta.env.VITE_APP_URL
    || 'https://app.poolit.club';
  return `${base.replace(/\/$/, '')}/launch?t=${encodeURIComponent(token)}`;
}

/**
 * Build an Android Chrome `intent://` URL for the launch link: Chrome opens it
 * when present, `S.browser_fallback_url` (the same /launch?t= link) opens in
 * the current browser when Chrome is absent.
 *
 * For a REAL browser tab only (the "install in Chrome" sheet of Samsung
 * Internet, Mi, Oppo/vivo, Opera, Firefox, UC). Never hand it to
 * `adapter.openExternalLink` inside the Mini App — see the header.
 *
 * Reference: https://developer.chrome.com/docs/android/intents
 *
 * @param {string} token
 * @returns {string} intent:// URL
 */
export function buildChromeIntentUrl(token) {
  return chromeIntentUrlFor(buildLaunchUrl(token));
}

/**
 * Cheap, sufficient Android detection. We don't need fingerprint-grade
 * precision — the only consumer is the "Open in Chrome" affordance, and the
 * intent URL's `browser_fallback_url` covers the false-negative case. The one
 * implementation lives in platform/capabilities.js; re-exported here so the
 * launch callers keep their import.
 */
export { isAndroid } from '@/platform/capabilities.js';

/**
 * How long a tap may wait for its launch token. Telegram drops `openLink` when
 * the last touch is more than 10 s old, so a mint that lands later would call
 * it for nothing — the member sees no browser and no error. 8 s leaves room
 * for the open itself.
 */
export const MINT_TIMEOUT_MS = 8_000;

/**
 * Mint a single-use launch token within `timeoutMs`, or reject with
 * `{ status: 0, code: 'timeout' }`. A token that arrives after the deadline is
 * dropped: the caller must never open late. `expiresIn` is the server's
 * lifetime in seconds (60 today), for callers that keep a link fresh.
 *
 * @param {{ timeoutMs?: number }} [options]
 * @returns {Promise<{ token: string, expiresIn: number }>}
 * @throws {{ status: number, code?: string, message: string }}
 */
export async function mintLaunchToken({ timeoutMs = MINT_TIMEOUT_MS } = {}) {
  let timer = null;
  const deadline = new Promise((_resolve, reject) => {
    timer = setTimeout(() => {
      reject({ status: 0, code: 'timeout', message: `Launch token not issued within ${timeoutMs} ms` });
    }, timeoutMs);
  });
  try {
    const { token, expires_in: expiresIn } = await Promise.race([issue(), deadline]);
    return { token, expiresIn: Number(expiresIn) || 60 };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * One-shot, from a tap: mint a launch token (bounded, see MINT_TIMEOUT_MS) and
 * open the launch link in a real browser. `chrome: true` asks Telegram for
 * Chrome (`tryBrowser: 'chrome'`, Android only — a missing Chrome fails
 * silently there, so callers keep a plain second button); `false` is the
 * browser Telegram's own settings pick.
 *
 * Throws the shaped error from `mintLaunchToken()` (`code: 'timeout'` for a
 * slow mint); `openExternalLink` itself does not throw.
 *
 * @param {{ chrome?: boolean }} [options]
 * @returns {Promise<string>} The URL handed to the browser.
 */
export async function launchInBrowser({ chrome = false } = {}) {
  const { token } = await mintLaunchToken();
  const url = buildLaunchUrl(token);
  adapter.openExternalLink(url, chrome ? { browser: 'chrome' } : {});
  return url;
}

/**
 * The way back into Telegram when the browser has no session: the bot's
 * `tg://resolve` link. ONE implementation for LaunchPage's «افتح تيليجرام مرة
 * أخرى» and the collector's «افتح يومك من تيليجرام» card (collector-app P2-B,
 * L-D), so both take the same path.
 *
 * @param {{ fallback?: () => void }} [options] - run when no bot is configured
 * @returns {boolean} true when the Telegram link was opened
 */
export function openTelegramBot({ fallback } = {}) {
  const bot = import.meta.env.VITE_TELEGRAM_BOT_USERNAME;
  if (bot) {
    window.location.href = `tg://resolve?domain=${bot}`;
    return true;
  }
  fallback?.();
  return false;
}
